/**
 * Platform flag — the single source of truth for desktop-vs-browser branching
 * (Task 4093 step 1).
 *
 * Stream has two build targets from one source tree:
 *
 *   - **Desktop** (`pnpm tauri:dev` / `tauri build`) — the full engineering app
 *     running inside the Tauri shell, with the Rust layer, Keychain, pop-out
 *     `WebviewWindow`s and the complete route set.
 *   - **Browser** (`pnpm build:web`) — the narrower support portal, six
 *     surfaces, no Tauri anywhere.
 *
 * ## Why this is a build-time constant, not a runtime check
 *
 * Task 4092 used a runtime check (`'__TAURI_INTERNALS__' in window`) so a single
 * bundle could serve both targets while the seam was being built. That is no
 * longer good enough. A runtime check *hides* desktop code; it does not *remove*
 * it. The `@tauri-apps/*` imports stay in the module graph, get bundled, and
 * execute their top-level side effects in a browser where those APIs do not
 * exist — which is exactly the failure this task exists to prevent.
 *
 * `IS_TAURI` below is replaced verbatim with `true` or `false` at build time by
 * Vite's `define` (see `vite.config.ts`). Rollup then folds the constant and
 * drops the dead branch **and its imports** from the browser bundle. That is why
 * the acceptance test for this task is bundle *content*, not UI behaviour.
 *
 * ## How to use it
 *
 * For dead-code elimination to work, the check must be statically analysable.
 * Rollup can only drop an import if the branch guarding it is a plain
 * `if (IS_TAURI)` / ternary over the constant — so:
 *
 * ```ts
 * // GOOD — Rollup folds this and drops the dynamic import entirely.
 * if (IS_TAURI) {
 *   const { invoke } = await import('@tauri-apps/api/core')
 *   await invoke('some_command')
 * }
 *
 * // BAD — a static top-level import is bundled no matter what guards its use.
 * import { invoke } from '@tauri-apps/api/core'
 * if (IS_TAURI) await invoke('some_command')
 * ```
 *
 * Never re-derive the platform by sniffing `window` — import from here. Scattered
 * `typeof window.__TAURI__` checks are what this module replaces.
 */

/**
 * True when this bundle targets the Tauri desktop shell.
 *
 * Replaced with a literal `true`/`false` at build time. In the browser build
 * this is `false`, and every `if (IS_TAURI)` branch — plus any `await import()`
 * of a Tauri module inside it — is eliminated from the output.
 *
 * The `?? ` fallback covers non-Vite consumers of this module: Vitest transforms
 * the source without our `define`, so tests see the runtime detection they had
 * before. Production builds never reach it, because `define` performs a textual
 * substitution *before* this expression is parsed.
 */
export const IS_TAURI: boolean =
  typeof __STREAM_IS_TAURI__ !== 'undefined'
    ? __STREAM_IS_TAURI__
    : typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window

/** True in the browser support portal. The inverse of {@link IS_TAURI}. */
export const IS_BROWSER: boolean = !IS_TAURI
